// =============================================================================
// Newsletter routing engine — PRD-01 §2.6 #8.
//
// Pure function: takes a Classification + LabInputs and returns the routing
// payload for Beehiiv (custom_fields + matched-issue list). The actual rules
// + issue catalog live in `content/newsletter-routing.json` so they can be
// edited without a code change (per PRD).
//
// Two consumers:
//
//   1. The results-page front-end calls `routeNewsletter()` after classify()
//      and uses `payload.beehiivCustomFields` to populate hidden inputs on the
//      exit-intent + footer subscribe forms. The Worker forwards those fields
//      to Beehiiv.
//
//   2. The same `routeNewsletter()` call returns `payload.matchedIssues` —
//      the top N (default 3) FF newsletter issues most relevant to the user's
//      result. The renderer surfaces these in the "Things you can explore on
//      your own → Keep learning" off-ramp on results.html. (UI wiring is
//      separate from this engine.)
//
// Deterministic + side-effect-free; safe to call repeatedly.
// =============================================================================

import routingData from '../../content/newsletter-routing.json' with { type: 'json' };
import type {
  Classification,
  ConfidenceTier,
  LabInputs,
  SeverityTier,
} from './types.js';

// -----------------------------------------------------------------------------
// Public types
// -----------------------------------------------------------------------------

/** A matched FF newsletter issue surfaced to the reader. */
export interface MatchedIssue {
  /** Stable slug used in the rules file. */
  id: string;
  /** Display title (sentence case). */
  title: string;
  /** Absolute URL to the published issue on femmefortified.com. */
  url: string;
  /** Sum of weights from every rule that contributed this issue. */
  score: number;
  /** Routing tags whose rules contributed this issue (deduplicated). */
  tags: string[];
}

/**
 * Six-bucket grouping used by Beehiiv automation filters. Maps the engine's
 * 19 possible patternId values down to the six buckets that drive nurture
 * routing. See `mapPatternIdToBucket()` for the full mapping table.
 */
export type Bucket =
  | 'iron_healthy'
  | 'iron_running_low'
  | 'iron_deficiency_anemia'
  | 'inflammation_masking'
  | 'overload_trigger'
  | 'insufficient_data';

/**
 * Beehiiv subscriber custom-field payload. PRD-01 §2.10 / §3.7 references
 * these as "pattern_bucket, life_stage, track, severity_flag" — the
 * granular pattern field is renamed here to `iron_pattern_id` and the
 * grouping is exposed as `iron_pattern_bucket` for clarity. Downstream
 * automations also use confidence_tier, routing_tags, severity_override.
 *
 * All values are strings — Beehiiv custom fields are typed at create time on
 * the dashboard side, but the API accepts string values regardless of type.
 */
export interface BeehiivCustomFields {
  /**
   * Engine-internal patternId at full granularity (e.g.
   * "stage_3_iron_deficient_erythropoiesis"). Preserved on every subscriber
   * for analytics + future reuse. Beehiiv automations should NOT filter on
   * this — there are 19 possible values. Filter on `iron_pattern_bucket`
   * instead.
   */
  iron_pattern_id: string;
  /**
   * One of six buckets used by Beehiiv automation filters. Derived from
   * `iron_pattern_id` via `mapPatternIdToBucket()`. This is the field
   * nurture automations branch on — single, stable value per nurture
   * variant.
   */
  iron_pattern_bucket: Bucket;
  severity_flag: SeverityTier;
  confidence_tier: ConfidenceTier;
  life_stage: string;
  track: string; // omnivore | plant_forward
  /** Comma-separated list of matched routing tags, e.g. "default,postmenopausal,supplementing". */
  routing_tags: string;
  /** "true" when severity is urgent; Beehiiv automations branch off this for the override email arc. */
  severity_override: 'true' | 'false';
}

export interface RoutingPayload {
  /** Top-N matched issues (default N = 3). Sorted by score descending. */
  matchedIssues: MatchedIssue[];
  /** Flat list of all routing tags fired for the user (deduplicated, sorted). */
  routingTags: string[];
  /** Beehiiv custom_field payload. */
  beehiivCustomFields: BeehiivCustomFields;
  /** True when the urgent-severity override arc should fire instead of the standard nurture sequence. */
  severityOverride: boolean;
}

// -----------------------------------------------------------------------------
// Rules-file shape — kept narrow on purpose. Validated at module load.
// -----------------------------------------------------------------------------

interface IssueDef {
  title: string;
  url: string;
}

type Trigger =
  | { kind: 'always' }
  | { kind: 'completeness'; value: string }
  | { kind: 'patternIdAny'; values: string[] }
  | { kind: 'anyLabPresent'; fields: string[] }
  | { kind: 'lifeStage'; value: string }
  | { kind: 'symptomAny'; values: string[] }
  | { kind: 'symptomAll'; values: string[] }
  | { kind: 'normalLabsButSymptoms' }
  | { kind: 'absorptionCondition'; value: string }
  | { kind: 'ironSupplementation'; value: string }
  | { kind: 'diet'; value: string }
  | { kind: 'coffeeTeaHabit'; value: string };

interface Rule {
  tag: string;
  weight: number;
  trigger: Trigger;
  issues: string[];
}

interface RoutingFile {
  version: number;
  spec?: string;
  issues: Record<string, IssueDef>;
  rules: Rule[];
  settings?: {
    maxIssues?: number;
    alwaysIncludeTagInBeehiiv?: string[];
  };
}

// Cast through `unknown` so a typo in the JSON won't compile-pass silently.
const RULES = routingData as unknown as RoutingFile;
const MAX_ISSUES = RULES.settings?.maxIssues ?? 3;
const ALWAYS_INCLUDE_TAGS: readonly string[] = RULES.settings?.alwaysIncludeTagInBeehiiv ?? [];

// -----------------------------------------------------------------------------
// Pattern → bucket mapping
//
// The engine emits 15 substantive pattern IDs (patterns.ts) plus 4 meta-pattern
// IDs (arbitrate.ts: mixed_picture_multiple_patterns, insufficient_data,
// no_established_pattern, severity_urgent_override). All 19 collapse into 6
// buckets here for Beehiiv automation routing. The granular patternId stays
// in the `iron_pattern_id` field for analytics; `iron_pattern_bucket` is
// what filters branch on.
//
// When a new pattern ID is added to the engine, add it here too. Until then
// unknown IDs fall back to `insufficient_data` — the safest default since the
// insufficient_data nurture content is "complete your panel" framing that's
// reasonably useful for any unclassified case.
// -----------------------------------------------------------------------------

const PATTERN_TO_BUCKET: Readonly<Record<string, Bucket>> = {
  // Healthy iron status
  stage_0_iron_replete: 'iron_healthy',

  // Iron running low — depletion without anemia, or signals consistent with it
  stage_1_incipient_id: 'iron_running_low',
  stage_2_iron_depletion_no_anemia: 'iron_running_low',
  microcytic_normal_iron_studies: 'iron_running_low',
  pregnancy_t1_at_risk_for_t3_deficiency: 'iron_running_low',

  // Iron deficiency anemia
  stage_3_iron_deficient_erythropoiesis: 'iron_deficiency_anemia',
  stage_4_iron_deficiency_anemia: 'iron_deficiency_anemia',

  // Inflammation in the picture (ferritin masked, TSAT clarifies)
  inflammation_masks_iron_status: 'inflammation_masking',
  functional_iron_deficiency: 'inflammation_masking',
  anemia_of_chronic_disease_classic: 'inflammation_masking',
  anemia_with_replete_ferritin: 'inflammation_masking',

  // Iron overload signal — clinical workup
  iron_overload_trigger_premenopausal: 'overload_trigger',
  iron_overload_trigger_postmenopausal: 'overload_trigger',
  very_high_ferritin_any_stage: 'overload_trigger',

  // Macrocytic anemia signals B12/folate, not iron — route to "complete the
  // panel" rather than the IDA bucket (which would mislead).
  macrocytic_anemia_signal: 'insufficient_data',

  // Meta-patterns — none of these have a single iron pattern story.
  // Routing them to insufficient_data sends "what's worth testing next"
  // content, which is universally useful when the picture isn't clear.
  insufficient_data: 'insufficient_data',
  no_established_pattern: 'insufficient_data',
  mixed_picture_multiple_patterns: 'insufficient_data',
  severity_urgent_override: 'insufficient_data',
};

/**
 * Map an engine patternId to one of six buckets used by Beehiiv automation
 * filters. Unknown / unmapped IDs fall back to `insufficient_data`.
 */
export function mapPatternIdToBucket(patternId: string): Bucket {
  return PATTERN_TO_BUCKET[patternId] ?? 'insufficient_data';
}

// -----------------------------------------------------------------------------
// Trigger evaluators — each returns true when the user matches.
// -----------------------------------------------------------------------------

function fires(trigger: Trigger, classification: Classification, inputs: LabInputs): boolean {
  switch (trigger.kind) {
    case 'always':
      return true;
    case 'completeness':
      return classification.completeness === trigger.value;
    case 'patternIdAny':
      return trigger.values.includes(classification.patternId);
    case 'anyLabPresent':
      return trigger.fields.some((field) => {
        const v = (inputs as unknown as Record<string, unknown>)[field];
        return v !== undefined && v !== null;
      });
    case 'lifeStage':
      return inputs.lifeStage === trigger.value;
    case 'symptomAny':
      return (inputs.symptoms || []).some((s) => trigger.values.includes(s));
    case 'symptomAll':
      return trigger.values.every((s) =>
        (inputs.symptoms || []).includes(s as NonNullable<LabInputs['symptoms']>[number]),
      );
    case 'normalLabsButSymptoms':
      // "Values look 'normal' but symptoms real" — research-suggested zone is healthy
      // (ferritin ≥ 50) AND user reports at least one symptom.
      return (inputs.ferritin ?? 0) >= 50 && (inputs.symptoms || []).length > 0;
    case 'absorptionCondition':
      return (inputs.absorptionConditions || []).includes(trigger.value);
    case 'ironSupplementation':
      return inputs.ironSupplementation === trigger.value;
    case 'diet':
      return inputs.diet === trigger.value;
    case 'coffeeTeaHabit':
      return inputs.coffeeTeaHabit === trigger.value;
  }
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * Run the routing engine for a given classification + inputs.
 * Pure / deterministic — no DOM access, no side effects.
 */
export function routeNewsletter(
  classification: Classification,
  inputs: LabInputs,
): RoutingPayload {
  // Score every issue by summing matched-rule weights. Track which rules
  // (tags) contributed so we can label issues + send tag list to Beehiiv.
  const issueScore: Record<string, number> = {};
  const issueTags: Record<string, Set<string>> = {};
  const firedTags: Set<string> = new Set();

  for (const rule of RULES.rules) {
    if (!fires(rule.trigger, classification, inputs)) continue;
    firedTags.add(rule.tag);
    for (const issueId of rule.issues) {
      issueScore[issueId] = (issueScore[issueId] ?? 0) + rule.weight;
      const tagSet = issueTags[issueId] ?? new Set<string>();
      tagSet.add(rule.tag);
      issueTags[issueId] = tagSet;
    }
  }

  // Sort matched issues by score desc, then by id for stable tiebreak.
  const ranked: MatchedIssue[] = Object.entries(issueScore)
    .map(([id, score]) => {
      const def = RULES.issues[id];
      if (!def) {
        // Rule references an unknown issue id — skip rather than crash.
        return null;
      }
      return {
        id,
        title: def.title,
        url: def.url,
        score,
        tags: Array.from(issueTags[id] ?? []).sort(),
      } satisfies MatchedIssue;
    })
    .filter((x): x is MatchedIssue => x !== null)
    .sort((a, b) => (b.score - a.score) || a.id.localeCompare(b.id));

  const matchedIssues = ranked.slice(0, MAX_ISSUES);

  // Routing tags = every fired tag, plus any always-include-in-Beehiiv tags
  // declared in settings (so e.g. "default" is always present even if its
  // weight didn't promote it into the top-N issue picks).
  const routingTags = Array.from(new Set([...firedTags, ...ALWAYS_INCLUDE_TAGS])).sort();

  const severityOverride = classification.severity === 'urgent';

  const beehiivCustomFields: BeehiivCustomFields = {
    iron_pattern_id: classification.patternId,
    iron_pattern_bucket: mapPatternIdToBucket(classification.patternId),
    severity_flag: classification.severity,
    confidence_tier: classification.confidenceTier,
    life_stage: inputs.lifeStage,
    track: inputs.diet,
    routing_tags: routingTags.join(','),
    severity_override: severityOverride ? 'true' : 'false',
  };

  return {
    matchedIssues,
    routingTags,
    beehiivCustomFields,
    severityOverride,
  };
}

// Re-export the raw catalog for tooling / docs / tests that want to assert
// against the rules file directly.
export const NEWSLETTER_ROUTING_CATALOG: Readonly<RoutingFile> = RULES;
